/**
 * LibreTest — secuencia libre de estímulos.
 *
 * Mecánica (replica LibreTestTest.aspx.cs):
 * - Cada item de la secuencia puede ser:
 *   • consigna (es_consigna=true): texto + botón continuar
 *   • lista de palabras (Valor con ";"): mostrar palabras una a una (tiempo_miliPalabra ms),
 *     luego input libre de recall
 *   • pregunta booleana (es_pregunta_boolena=true): texto + botones Sí/No
 *   • estímulo único: mostrar (tiempo_mili ms) y auto-avanzar, o input si tiene respuesta esperada
 * - Al finalizar: pantalla de conclusión y opción de ver resultados
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTestConfig, TYPE_TO_API_PATH } from '../../api/client';
import TestLayout, {
  DoneScreen, YesNoButtons, ContinueButton, LoadingScreen, ErrorScreen, imageUrl, useSpeedMultiplier,
  useFocusOnStep,
} from './shared/TestLayout';
import SaveResults from './shared/SaveResults';
import SafeHtml from './shared/SafeHtml';
import { registrarUso } from '../../api/usoClient';
import { navigateAfterTest } from '../../api/ensayosClient';

const PHASE = { LOADING: 'loading', RUNNING: 'running', DONE: 'done' };

export default function TestRunLibreTest() {
  const { lenguajeId, categoriaId, tareaId, type, testId } = useParams();
  const navigate = useNavigate();
  const apiType = TYPE_TO_API_PATH[type] ?? type;

  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(PHASE.LOADING);

  // posición en la secuencia
  const [seqIndex, setSeqIndex] = useState(0);
  // para listas de palabras: índice dentro de la lista
  const [wordIndex, setWordIndex] = useState(0);
  const [showingWords, setShowingWords] = useState(true);
  const [currentInput, setCurrentInput] = useState('');

  // resultados
  const [responses, setResponses] = useState([]);

  // pantalla de resultados
  const [showResults, setShowResults] = useState(false);

  const timerRef      = useRef(null);
  const usageLoggedRef = useRef(false);
  const responsesRef   = useRef([]);
  responsesRef.current = responses;

  useEffect(() => {
    getTestConfig(apiType, testId)
      .then((data) => {
        setConfig(data);
        (data.ListaValores?.Secuencia ?? []).forEach((item) => {
          [item.rutaImagen1, item.rutaImagen2].forEach((url) => {
            if (url) { const img = new Image(); img.src = imageUrl(url); }
          });
        });
        setPhase(PHASE.RUNNING);
      })
      .catch((e) => setError(e.message));
  }, [apiType, testId]);

  const goBack = useCallback(() => {
    navigateAfterTest(
      navigate,
      `/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${tareaId}`,
    );
  }, [navigate, lenguajeId, categoriaId, tareaId]);

  // Logging de uso
  useEffect(() => {
    if (phase !== PHASE.DONE || usageLoggedRef.current) return;
    usageLoggedRef.current = true;
    const r = responsesRef.current;
    registrarUso({
      tipo_test:   `LT${testId}`,
      test_id:     testId,
      tarea_id:    tareaId,
      nombre_test: config?.descripcion ?? '',
      completada:  true,
      total_items: r.length,
      correctas:   r.filter((x) => x.correcta === 'CORRECTO').length,
      errores:     r.filter((x) => x.correcta === 'INCORRECTO').length,
    });
  }, [phase, testId, tareaId, config]);

  const handleExit = useCallback(() => {
    if (!usageLoggedRef.current) {
      usageLoggedRef.current = true;
      const r = responsesRef.current;
      registrarUso({
        tipo_test:   `LT${testId}`,
        test_id:     testId,
        tarea_id:    tareaId,
        nombre_test: config?.descripcion ?? '',
        completada:  false,
        total_items: r.length,
        correctas:   r.filter((x) => x.correcta === 'CORRECTO').length,
        errores:     r.filter((x) => x.correcta === 'INCORRECTO').length,
      });
    }
    goBack();
  }, [goBack, testId, tareaId, config]);

  const speedMultiplier = useSpeedMultiplier();
  const secuencia = config?.ListaValores?.Secuencia ?? [];
  const tiempoPalabra = (config?.tiempo_miliPalabra ?? 1500) / speedMultiplier;
  const tiempoItem = (config?.tiempo_mili ?? 1500) / speedMultiplier;
  const conclusiones = config?.Concluciones ?? '';

  const currentItem = secuencia[seqIndex];
  const isWordList = !!currentItem?.es_secuencia;
  const wordList = isWordList && currentItem?.Valor
    ? currentItem.Valor.split(';').map((s) => s.trim()).filter(Boolean)
    : null;
  const isConsigna = !!currentItem?.es_consigna;
  const isBoolQuestion = !!currentItem?.es_pregunta_boolena;
  const hasExpectedAnswer = currentItem?.respuesta != null && currentItem?.respuesta !== '';

  const showRecallInput = phase === PHASE.RUNNING && !!currentItem && !isConsigna && isWordList && !showingWords;
  const showAnswerInput = phase === PHASE.RUNNING && !!currentItem && !isConsigna && !isWordList && !isBoolQuestion && hasExpectedAnswer;
  const answerInputRef = useFocusOnStep(
    showRecallInput || showAnswerInput,
    `${seqIndex}-${showingWords ? 'words' : 'answer'}`,
  );

  const advanceSeq = useCallback(() => {
    setSeqIndex((i) => {
      const next = i + 1;
      if (next >= secuencia.length) { setPhase(PHASE.DONE); return i; }
      return next;
    });
    setWordIndex(0);
    setShowingWords(true);
    setCurrentInput('');
  }, [secuencia.length]);

  const recordAndAdvance = useCallback((value, correct) => {
    setResponses((r) => [...r, {
      pregunta: currentItem?.Valor ?? '',
      respuesta: value,
      correcta: correct,
    }]);
    advanceSeq();
  }, [currentItem, advanceSeq]);

  // Auto-avanzar palabras en lista
  useEffect(() => {
    if (phase !== PHASE.RUNNING || !isWordList || !showingWords) return;
    timerRef.current = setTimeout(() => {
      if (wordIndex < wordList.length - 1) {
        setWordIndex((i) => i + 1);
      } else {
        setShowingWords(false);
      }
    }, tiempoPalabra);
    return () => clearTimeout(timerRef.current);
  }, [phase, isWordList, showingWords, wordIndex, wordList?.length, tiempoPalabra]);

  // Auto-avanzar estímulo único sin respuesta
  useEffect(() => {
    if (phase !== PHASE.RUNNING || !currentItem || isConsigna || isWordList || isBoolQuestion || hasExpectedAnswer) return;
    timerRef.current = setTimeout(() => advanceSeq(), tiempoItem);
    return () => clearTimeout(timerRef.current);
  }, [phase, currentItem, isConsigna, isWordList, isBoolQuestion, hasExpectedAnswer, seqIndex, tiempoItem, advanceSeq]);

  const handleRetry = () => {
    setSeqIndex(0); setWordIndex(0); setShowingWords(true);
    setCurrentInput(''); setResponses([]); setShowResults(false);
    setPhase(PHASE.RUNNING);
  };

  if (error) return <ErrorScreen message={error} onBack={goBack} />;
  if (phase === PHASE.LOADING || !config) return <LoadingScreen />;

  if (phase === PHASE.DONE) {
    const registros = responses.map((r) => ({
      pregunta:        r.pregunta,
      respuesta:       r.respuesta ?? '',
      respuestaUsuario:r.respuesta ?? '',
      correcta:        r.correcta === 'CORRECTO',
      tiempoRespuesta: '',
    }));
    const tipo = `LT${testId}`;

    return (
      <DoneScreen conclusiones={showResults ? null : conclusiones} onRetry={handleRetry} onBack={goBack}>
        <SaveResults tipo={tipo} registros={registros} />
        {showResults ? (
          <div className="text-left mt-4 w-full">
            <h3 className="text-lg font-semibold mb-3 text-center">Resultados</h3>
            {responses.length === 0
              ? <p className="text-slate-500 text-center">Sin respuestas registradas.</p>
              : (
                <table className="w-full table-fixed text-sm border border-slate-200 rounded-lg overflow-hidden mb-4">
                  <thead className="bg-slate-100 text-slate-700 font-semibold">
                    <tr>
                      <th className="w-1/2 px-4 py-2 text-left">Pregunta</th>
                      <th className="w-1/4 px-4 py-2 text-left">Respuesta</th>
                      <th className="w-1/4 px-4 py-2 text-left">Resultado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {responses.map((r, i) => (
                      <tr key={i} className="text-slate-700">
                        <td className="px-4 py-3 align-top break-words">
                          <SafeHtml as="div" html={r.pregunta} />
                        </td>
                        <td className="px-4 py-3 align-top break-words">{r.respuesta || '—'}</td>
                        <td className={`px-4 py-3 align-top font-medium ${r.correcta === 'CORRECTO' ? 'text-green-600' : r.correcta === 'INCORRECTO' ? 'text-red-500' : 'text-slate-400'}`}>
                          {r.correcta || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
            <div className="flex justify-center">
              <button
                onClick={() => setShowResults(false)}
                className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 cursor-pointer uppercase"
              >
                OCULTAR RESULTADOS
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowResults(true)}
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 mt-2 cursor-pointer"
          >
            QUIERO SABER MIS RESULTADOS
          </button>
        )}
      </DoneScreen>
    );
  }

  if (!currentItem) { setPhase(PHASE.DONE); return null; }

  return (
    <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>

      {/* CONSIGNA */}
      {isConsigna && (
        <>
          <SafeHtml
            as="p"
            html={currentItem.Valor}
            className="text-center text-slate-800 whitespace-pre-wrap leading-tight max-w-5xl mb-4"
            style={{ fontSize: '33px', fontWeight: 700 }}
          />
          {(currentItem.rutaImagen1 || currentItem.rutaImagen2) && (
            <div className="flex gap-8 items-center justify-center flex-wrap mb-6">
              {currentItem.rutaImagen1 && <img src={imageUrl(currentItem.rutaImagen1)} alt="" className="max-h-56 object-contain" />}
              {currentItem.rutaImagen2 && <img src={imageUrl(currentItem.rutaImagen2)} alt="" className="max-h-56 object-contain" />}
            </div>
          )}
          <ContinueButton onClick={advanceSeq} />
        </>
      )}

      {/* LISTA DE PALABRAS */}
      {!isConsigna && isWordList && (
        <>
          {showingWords ? (
            <SafeHtml
              as="div"
              html={wordList[wordIndex]}
              className="text-8xl font-medium text-center min-h-[4rem] flex items-center justify-center text-slate-800"
            />
          ) : (
            <div className="w-full max-w-2xl">
              <p className="text-slate-600 mb-4 text-center">Escriba todas las palabras que recuerde:</p>
              <textarea
                ref={answerInputRef}
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); recordAndAdvance(currentInput, ''); } }}
                className="w-full min-h-[120px] rounded-lg bg-white border border-slate-300 text-slate-800 p-4 text-xl focus:border-emerald-500 outline-none"
                placeholder="Palabras separadas por coma o espacio"
              />
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => recordAndAdvance(currentInput, '')}
                  className="border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity"
                >
                  <img src="/assets/img/Ok-icon.png" alt="Siguiente" className="h-20 w-auto" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* PREGUNTA BOOLEANA */}
      {!isConsigna && !isWordList && isBoolQuestion && (
        <>
          <div className="text-3xl font-medium text-center mb-4 text-slate-800 max-w-3xl">
            {currentItem.rutaImagen1 && (
              <img src={imageUrl(currentItem.rutaImagen1)} alt="" className="max-h-48 mx-auto mb-4 object-contain" />
            )}
            <SafeHtml as="span" html={currentItem.Valor} />
          </div>
          <YesNoButtons
            onYes={() => recordAndAdvance('SI', currentItem.es_verdadero ? 'CORRECTO' : 'INCORRECTO')}
            onNo={() => recordAndAdvance('NO', currentItem.es_verdadero ? 'INCORRECTO' : 'CORRECTO')}
          />
        </>
      )}

      {/* ESTÍMULO CON RESPUESTA DE TEXTO */}
      {!isConsigna && !isWordList && !isBoolQuestion && hasExpectedAnswer && (
        <>
          <SafeHtml as="div" html={currentItem.Valor} className="text-4xl font-bold text-center mb-6 text-slate-800" />
          {(currentItem.rutaImagen1 || currentItem.rutaImagen2) && (
            <div className="flex gap-8 items-center justify-center flex-wrap mb-6">
              {currentItem.rutaImagen1 && <img src={imageUrl(currentItem.rutaImagen1)} alt="" className="max-h-56 object-contain" />}
              {currentItem.rutaImagen2 && <img src={imageUrl(currentItem.rutaImagen2)} alt="" className="max-h-56 object-contain" />}
            </div>
          )}
          <input
            ref={answerInputRef}
            type="text"
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const correct = currentInput.trim().toUpperCase() === String(currentItem.respuesta).trim().toUpperCase()
                  ? 'CORRECTO' : 'INCORRECTO';
                recordAndAdvance(currentInput, correct);
              }
            }}
            className="max-w-md w-full rounded-lg bg-white border border-slate-300 text-slate-800 p-4 text-center text-lg focus:border-emerald-500 outline-none"
            placeholder="Ingrese su respuesta"
          />
          <button
            onClick={() => {
              const correct = currentInput.trim().toUpperCase() === String(currentItem.respuesta).trim().toUpperCase()
                ? 'CORRECTO' : 'INCORRECTO';
              recordAndAdvance(currentInput, correct);
            }}
            className="border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity mt-4"
          >
            <img src="/assets/img/Ok-icon.png" alt="Siguiente" className="h-20 w-auto" />
          </button>
        </>
      )}

      {/* ESTÍMULO SIN RESPUESTA (auto-avanza) */}
      {!isConsigna && !isWordList && !isBoolQuestion && !hasExpectedAnswer && (
        <div className="text-3xl font-medium text-center text-slate-800">
          {currentItem.rutaImagen1 || currentItem.rutaImagen2 ? (
            <div className="flex gap-8 items-center justify-center flex-wrap">
              {currentItem.rutaImagen1 && <img src={imageUrl(currentItem.rutaImagen1)} alt="" className="max-h-56 object-contain" />}
              {currentItem.rutaImagen2 && <img src={imageUrl(currentItem.rutaImagen2)} alt="" className="max-h-56 object-contain" />}
            </div>
          ) : (
            <SafeHtml as="span" html={currentItem.Valor} />
          )}
        </div>
      )}

    </TestLayout>
  );
}
